import type { TenantDetail } from '@order-pilot/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { SectionCard } from '@/components/SectionCard';
import { tenantKeys, updateTenant } from '@/features/tenants/api';
import { getErrorMessage } from '@/lib/api';

/**
 * The SA's switch for the smart menu (view-only QR menu). Off by default —
 * not every tenant buys it. Switching it on only makes the feature AVAILABLE:
 * the tenant still turns each branch's menu on in their own portal.
 */
export function SmartMenuSection({ tenant }: { tenant: TenantDetail }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (enabled: boolean) => updateTenant(tenant.id, { smartMenuEnabled: enabled }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tenantKeys.all }),
  });
  const on = tenant.smartMenuEnabled;

  return (
    <SectionCard
      headingId="tenant-smart-menu"
      title="Smart menu"
      description="A view-only menu customers open from a QR code on the table."
    >
      <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">{on ? 'Available' : 'Not available'}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {on
              ? 'The tenant can switch the menu on for each branch in their portal.'
              : 'Switch on to let this tenant publish a menu for their branches.'}
          </p>
          {mutation.isError ? (
            <div className="mt-2">
              <Alert tone="error">{getErrorMessage(mutation.error)}</Alert>
            </div>
          ) : null}
        </div>
        <Button
          size="sm"
          variant={on ? 'secondary' : 'primary'}
          loading={mutation.isPending}
          onClick={() => mutation.mutate(!on)}
        >
          {on ? 'Switch off' : 'Switch on'}
        </Button>
      </div>
    </SectionCard>
  );
}
